import createSelector from 'lib/web/createSelector'
import { getCartParticipants } from './cart/cartSelectors'
import {
  getBaggageTotal,
  getBnblPaidTotal,
  getCompletedPackageTotal,
  getDiscountTotal,
  getExperiencesTotal,
  getFlightsTotal,
  getPackageBaseTotal,
  getSurchargeTotal,
} from './cart/cartAmountSelectors'
import { arrayToMap, fillArray, partitionBy } from 'lib/array/arrayUtils'
import { isCoverGeniusEnabled } from 'lib/insuranceHelpers'
import { isPackageDomestic } from 'lib/offer/isDomestic'
import { getTotalsByItemTypes } from 'lib/getPurchaseTotals'
import uuidV4 from 'lib/string/uuidV4Utils'
import {
  PRODUCT_ID_CANCELLATION_AND_BAGGAGE,
  PRODUCT_ID_COMPREHENSIVE,
  PRODUCT_ID_OVERSEA_MEDICAL,
} from 'constants/insurance'

export const getInitialTravellers = createSelector(
  (state: App.State) => getCartParticipants(state),
  (state: App.State) => state.auth.account,
  (state: App.State) => state.cart.flight.passengers,
  (travellerCount, account, passengers) => {
    const travellers: Array<App.InsuranceTraveller> = []
    const [passengerAdults, passengerDependants] = partitionBy(passengers, p => p.type === 'adult')

    if (travellerCount.adults > 0) {
      travellers.push(...fillArray<App.InsuranceTraveller>(
        travellerCount.adults, (i) => {
          const passenger = passengerAdults[i] || ({} as App.Passenger)
          const isPrimary = i === 0

          return {
            id: uuidV4(),
            isPrimary,
            givenName: passenger.firstName || (isPrimary ? account.givenName : undefined),
            surname: passenger.lastName || (isPrimary ? account.surname : undefined),
            dateOfBirth: passenger.birthDate || (isPrimary ? account.dob : undefined),
            title: passenger.title,
            isAustralianResident: passenger.nationality ? passenger.nationality === 'AU' : undefined,
          }
        }))
    }

    const dependantsCount = travellerCount.children + travellerCount.infants

    if (dependantsCount > 0) {
      travellers.push(...fillArray(dependantsCount, (i) => {
        const passenger = passengerDependants[i] || ({} as App.Passenger)

        return {
          id: uuidV4(),
          givenName: passenger.firstName,
          surname: passenger.lastName,
          dateOfBirth: passenger.birthDate,
          title: passenger.title,
          isAustralianResident: passenger.nationality ? passenger.nationality === 'AU' : undefined,
          isDependant: true,
        }
      }))
    }

    return travellers
  },
)

export const getAcceptedProduct = createSelector(
  (state: App.State) => state.cart.insurance.insuranceQuote,
  (state: App.State) => state.cart.insurance.insuranceAccepted,
  (state: App.State) => state.insurance.products,
  (quote, accepted, products = []) => {
    if (quote && accepted) {
      return products.find(p => p.id === quote.productId)
    }
    return undefined
  },
)

// generated based on the full protection quote, as we need the policy quotes
// no matter whether customer selects the policy or not
function getCustomiseOptionQuotes(
  productId: string,
  products: App.InsuranceProduct[],
  quotes: App.InsuranceQuote[],
): App.InsuranceQuote {
  const fullProtectionProduct = products.find(p => p.isFullProtection)
  const fullQuote = quotes.find(q => q.productId === fullProtectionProduct.id)
  if (fullQuote) {
    const lowestPricePolicy = fullQuote.policyQuotes.reduce((prev, curr) => {
      return prev.price < curr.price ? prev : curr
    })
    return {
      ...fullQuote,
      productId,
      startPrice: lowestPricePolicy.price,
      startPricePerPerson: lowestPricePolicy.pricePerPerson,
    }
  }
}

export const getQuoteForProduct = createSelector(
  (state: App.State) => state.cart.insurance.insuranceQuote,
  (state: App.State) => state.insurance.products,
  (state: App.State) => state.insurance.quotes,
  (state: App.State, productId: string) => productId,
  (cartQuote, products, quotes, productId) => {
    if (cartQuote && cartQuote.productId === productId) {
      return cartQuote
    } else {
      const product = products.find(p => p.id === productId)
      if (product) {
        // if not a customise option, just return the quote
        if (!product.policyOptions?.length) {
          return quotes.find(q => q.productId === productId)
        } else {
          return getCustomiseOptionQuotes(productId, products, quotes)
        }
      }
    }
  },
)

export const getSelectedQuote = createSelector(
  (state: App.State) => getQuoteForProduct(state, state.cart.insurance.insuranceProductId),
  (quote) => quote,
)

export const getSelectedProduct = createSelector(
  (state: App.State) => state.insurance.products,
  (state: App.State) => state.cart.insurance.insuranceProductId,
  (products, productId) => products.find(p => p.id === productId),
)

// In the back end we have a full list of products but they're not always available
// based on circumstances. e.g. domestic vs international or just plain not implemented
// in production yet
export const getAvailableProducts = createSelector(
  (state: App.State) => state.insurance.products,
  (state: App.State) => state.insurance.quotes,
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.cart.bedbank.offer,
  (products, quotes, LEoffer, pkg, currentRegionCode, bedbankOffer) => {
    if (!pkg && !bedbankOffer) {
      return []
    }
    const offer = LEoffer || bedbankOffer
    const quotesByProductId = arrayToMap(quotes, q => q.productId)
    let ret = products.filter(product => quotesByProductId.has(product.id))
    // Filter domestic and international products for cover genius
    if (isCoverGeniusEnabled(currentRegionCode)) {
      if (isPackageDomestic(offer, pkg, currentRegionCode)) {
        ret = ret.filter(product => product.id === PRODUCT_ID_CANCELLATION_AND_BAGGAGE)
      } else {
        ret = ret.filter(product => product.id === PRODUCT_ID_OVERSEA_MEDICAL || product.id === PRODUCT_ID_COMPREHENSIVE)
      }
    }
    return ret
  },
)

// getTotalPurchasePrice returns the amount that a customer paid for an escape.
// It is used by insurers to calculate the cancellation price.
export const getTotalPurchasePrice = createSelector(
  (state: App.State) => getBnblPaidTotal(state),
  (state: App.State) => getSurchargeTotal(state),
  (state: App.State) => getFlightsTotal(state),
  (state: App.State) => getExperiencesTotal(state),
  (state: App.State) => getBaggageTotal(state),
  (state: App.State) => getCompletedPackageTotal(state),
  (state: App.State) => getCompletedExtrasTotal(state),
  (state: App.State) => getPackageBaseTotal(state),
  (state: App.State) => getDiscountTotal(state),
  (state: App.State) => state.cart.insurance.insuranceOrderId,
  (state: App.State) => state.cart.bedbank.rate,
  (bnblTotal, surchargeTotal, flightsTotal, experiencesTotal, baggageTotal, completedPackageTotal, completedExtrasTotal, packageTotal, discountTotal, insuranceOrderId, bedbankRate) => {
    let total = bnblTotal + surchargeTotal + flightsTotal + experiencesTotal + baggageTotal

    // In an insurance upsell, sum up all completed package prices to correctly reflect
    // what the customer has paid.
    if (insuranceOrderId) {
      total = total + completedPackageTotal + completedExtrasTotal
    } else if (bedbankRate) {
      total = total + bedbankRate.value
    } else {
      total = total + packageTotal
    }

    return Math.max(total - discountTotal, 0)
  },
)

export const hasAvailableProduct = createSelector(
  (state: App.State) => state.cart.bedbank.offer,
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.cart.accommodation.package,
  (bedbankOffer, pkg) => {
    if (!pkg && !bedbankOffer) {
      return false
    }

    return true
  },
)

export const getProducts = createSelector(
  (state: App.State) => state.insurance.products,
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.cart.bedbank.offer,
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.geo.currentRegionCode,
  (products, LEoffer, bedbankOffer, pkg, currentRegionCode) => {
    if (!pkg && !bedbankOffer) {
      return []
    }

    const offer = LEoffer || bedbankOffer
    // Filter domestic and international products for cover genius
    if (isCoverGeniusEnabled(currentRegionCode)) {
      return isPackageDomestic(offer, pkg, currentRegionCode) ?
        products.filter(product => product.id === PRODUCT_ID_CANCELLATION_AND_BAGGAGE) :
        products.filter(product => product.id === PRODUCT_ID_OVERSEA_MEDICAL || product.id === PRODUCT_ID_COMPREHENSIVE)
    }
    return products
  },
)

/**
 * Calculates the total price of order flights and addons.
 */
export const getCompletedExtrasTotal = createSelector(
  (state: App.State) => state.cart.order,
  (order) => {
    if (order) {
      const totals = getTotalsByItemTypes(order)
      return totals.addonsTotal + totals.flightsTotal + totals.experiencesTotal
    }
    return 0
  },
)
